import { useCallback, useEffect, useRef, useState } from "react";
import { useBeforeUnload, useLocation, useNavigate } from "react-router";

type BlockerState = "idle" | "blocked" | "proceeding";

interface Blocker {
  state: BlockerState;
  proceed: () => void;
  reset: () => void;
}

/**
 * Custom hook to block navigation when a condition is met
 * @param shouldBlock Function that returns true if navigation should be blocked
 * @returns Blocker object with state, proceed, and reset functions
 */
export function useBlocker(shouldBlock: () => boolean): Blocker {
  const navigate = useNavigate();
  const location = useLocation();
  const [blockerState, setBlockerState] = useState<BlockerState>("idle");
  const pendingNavigationRef = useRef<{ path: string; replace?: boolean } | null>(null);
  const prevLocationRef = useRef(location);

  // Handle browser beforeunload event
  useBeforeUnload(
    useCallback(
      (event) => {
        if (shouldBlock()) {
          // Standard way to show browser confirmation dialog
          event.preventDefault();
          // For older browsers
          event.returnValue = "";
          return "";
        }
        return undefined;
      },
      [shouldBlock],
    ),
  );

  // Monitor location changes to detect navigation attempts
  useEffect(() => {
    // Skip initial render
    if (prevLocationRef.current === location) {
      prevLocationRef.current = location;
      return;
    }

    // If we should block and we're not already proceeding with navigation
    if (shouldBlock() && blockerState !== "proceeding") {
      // Store the current location for potential navigation
      pendingNavigationRef.current = {
        path: location.pathname + location.search + location.hash,
        replace: true, // Use replace to avoid adding to history
      };

      // Set state to blocked to trigger the confirmation dialog
      setBlockerState("blocked");

      // Navigate back to previous location
      window.history.go(-1);
    } else {
      // Update the previous location reference
      prevLocationRef.current = location;
    }
  }, [location, shouldBlock, blockerState]);

  // Function to proceed with the blocked navigation
  const proceed = useCallback(() => {
    if (blockerState !== "blocked" || !pendingNavigationRef.current) {
      return;
    }

    setBlockerState("proceeding");
    const { path, replace } = pendingNavigationRef.current;

    // Use setTimeout to ensure state updates before navigation
    setTimeout(() => {
      navigate(path, { replace });
      pendingNavigationRef.current = null;
      setBlockerState("idle");
    }, 0);
  }, [blockerState, navigate]);

  // Function to reset the blocker state
  const reset = useCallback(() => {
    pendingNavigationRef.current = null;
    setBlockerState("idle");
  }, []);

  return { state: blockerState, proceed, reset };
}
